import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import LoadingBoundary from "../../../components/LoadingBoundary";
import MessageInfo from "../../../components/MessageInfo";
import Snack from "../../../components/Snack";
import { DossiersLocationProps } from "../../../services/Api/DossierLocation";
import RenderError from "../../ErrorContainer";
import BlocInformation from "./BlocInformation";
import CriteresRecherche from "./CriteresRecherche";
import ResultatsRecherche from "./ResultatsRecherche";
import { useDashboard } from "./selectors";
import styles from "./styles";
import { buildSearch } from "./helpers";

export interface Pagination {
  orderBy: string;
  sortColumn: string;
  sortOrder: "asc" | "desc";
  page: number;
  nbrElementParPage: number;
}

export const DEFAULT_NBR_ELEMENT_PAR_PAGE = 20;
const DEFAULT_SORT_COLUMN = "";
const DEFAULT_SORT_ORDER = "asc";

const criteresDefaut: DossiersLocationProps = {};
const paginationDefaut: Pagination = {
  page: 0,
  nbrElementParPage: DEFAULT_NBR_ELEMENT_PAR_PAGE,
  orderBy: DEFAULT_SORT_COLUMN,
  sortColumn: "",
  sortOrder: "asc",
};

function Dashboard() {
  const {
    utilisateurConnecte,
    utilisateurFetching,
    accepterRefuserLocationError,
    dossiersLocation,
    resultFetching,
    isAccepterRefuserLocationLoading,
    genererError,
    genererLoading,
    resultError,
    idAdb,
    infoProfil,
    retourLocationAcceptee,
    redirectCreationBien,
    getDossiersLocation,
    setSelectedDossier,
    setDuplicationDossier,
    setAcceptationDossier,
    setAnnulerAcceptationDossier,
    supprimerErrorDossierLocation,
    setFiltreRechercheDossier,
    deleteDossierLocation,
    genererCertification,
    isSuccessAccepterDossier,
    isSuccessAnnulerAccepterDossier,
    onCloseConfirmationCreationDossierLocation,
    confirmationCreationDossierLocation,
    displayCandidatLocataire,
  } = useDashboard();

  const [criteres, setCriteres] = useState(criteresDefaut);
  const [pagination, setPagination] = useState(paginationDefaut);
  const [open, setOpen] = useState<boolean>(false);
  const [refLocation, setRefLocation] = useState<string>("");
  const [
    showRetourConfirmationDossierAccepte,
    setShowRetourConfirmationDossierAccepte,
  ] = useState<boolean>(false);

  useEffect(() => {
    lancerRecherche(true);
  }, []);

  useEffect(() => {
    if (isSuccessAccepterDossier || isSuccessAnnulerAccepterDossier) {
      lancerRecherche(false);
    }
  }, [isSuccessAccepterDossier, isSuccessAnnulerAccepterDossier]);
  useEffect(() => {
    if (retourLocationAcceptee?.message?.length > 0) {
      setShowRetourConfirmationDossierAccepte(true);
    }
  }, [retourLocationAcceptee]);

  const handleChangeCritere = (id: string, value: string | boolean) => {
    setCriteres({
      ...criteres,
      [id]: value,
    });
  };
  const handleRechercher = () => {
    // Si on lance la recherche par le bouton Rechercher (et donc changement de critères)
    // on réinitialise le numéro de page
    lancerRecherche(true);
  };

  const handleClose = () => {
    setShowRetourConfirmationDossierAccepte(false);
  };

  const lancerRecherche = (resetPage: boolean) => {
    // On construit la requête de recherche en cumulant les critères de recherche
    // et les infos de pagination
    const params = buildSearch(criteres, resetPage, pagination, idAdb);
    if (resetPage) {
      setPagination({
        ...pagination,
        page: 0,
      });
    }
    if (
      (utilisateurConnecte && utilisateurConnecte.role === "ADB") ||
      (utilisateurConnecte && utilisateurConnecte.role === "BESSE" && idAdb)
    ) {
      setFiltreRechercheDossier(params);
      getDossiersLocation(params);
    }
  };

  const handleChangePage = (page: number) => {
    setPagination({
      ...pagination,
      page,
    });
  };
  const handleChangeNbrElementParPage = (nbrElementParPage: number) => {
    setPagination({
      ...pagination,
      nbrElementParPage,
    });
  };

  useEffect(() => {
    lancerRecherche(false);
  }, [
    pagination.page,
    pagination.nbrElementParPage,
    pagination.sortColumn,
    pagination.sortOrder,
  ]);
  const handleChangeTri = (sortColumn: string, sortOrder: "asc" | "desc") => {
    setPagination({
      ...pagination,
      sortColumn,
      sortOrder,
    });
  };

  const navigate = useNavigate();
  const onSetDuplicationDossier = (ref: string) => {
    setDuplicationDossier(ref);
    navigate("/dossier-location/bien");
  };

  const handleAccepterLocation = (ref: string) => {
    setAcceptationDossier(ref);
  };

  const handleRefuserLocation = (ref: string) => {
    setAnnulerAcceptationDossier(ref);
  };

  const handleAccepterRefuserLocation = (dossierLocationItem: any) => {
    if (
      setAnnulerAcceptationDossier &&
      dossierLocationItem.locationAcceptee !== 2
    ) {
      setAnnulerAcceptationDossier(dossierLocationItem.refLocation);
    } else if (
      setAcceptationDossier &&
      dossierLocationItem.locationAcceptee === 2
    ) {
      setAcceptationDossier(dossierLocationItem.refLocation);
    }
  };

  const handleDelete = (ref: string) => {
    setOpen(true);
    setRefLocation(ref);
  };

  const onCancel = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    deleteDossierLocation(refLocation);
    setOpen(false);
    lancerRecherche(false);
  };

  const handleGenerer = (ref: string) => {
    genererCertification(ref);
  };

  const initialValues = {
    refDossier: criteres.refDossier,
    refMandat: criteres.refMandat,
    ville: criteres.ville,
    statut: criteres.statut,
    nomLocataire: criteres.nomLocataire,
    prenomLocataire: criteres.prenomLocataire,
    gli: criteres.gli,
  };

  const onlyBesse =
    utilisateurConnecte && utilisateurConnecte.role === "BESSE" && !idAdb;

  const infoAdbUtilisateurs = onlyBesse ? (
    <MessageInfo
      messageUrl="sélectionner un administrateur de bien"
      url="/mes-administrateurs-de-biens"
    >
      Pour visualiser le tableau d'un administrateur de bien, veuillez en
      sélectionner un :
    </MessageInfo>
  ) : (
    ""
  );
  return (
    <>
      <Grid className={"dashboard"} sx={styles}>
        <BlocInformation />
        <Grid container alignItems="center" style={{ marginTop: "3em" }}>
          <Button
            color="primary"
            variant="contained"
            className={"button"}
            onClick={redirectCreationBien}
          >
            <AddIcon />
          </Button>
          <Typography className={"infoText"}>
            Pour créer un nouveau dossier, cliquez sur + ou complétez les
            filtres
            <br /> ci-dessous pour afficher un dossier existant :
          </Typography>
        </Grid>
        {infoAdbUtilisateurs}
        <>
          <CriteresRecherche
            initialValues={initialValues}
            onChangeCritere={handleChangeCritere}
            onRechercher={handleRechercher}
            displayCandidatLocataire={displayCandidatLocataire}
          />
          <LoadingBoundary
            error={resultError}
            loading={resultFetching}
            onRetry={handleRechercher}
          >
            {dossiersLocation ? (
              <ResultatsRecherche
                onChangeNbrElementParPage={handleChangeNbrElementParPage}
                onChangePage={handleChangePage}
                displayCandidatLocataire={displayCandidatLocataire}
                onChangeTri={handleChangeTri}
                onDelete={handleDelete}
                onGenerer={handleGenerer}
                dossiersLocation={dossiersLocation.elements || []}
                searchCount={dossiersLocation.total || 0}
                alerteEnCoursSuppression={
                  dossiersLocation && dossiersLocation.alerteEnCoursSuppression
                    ? dossiersLocation.alerteEnCoursSuppression
                    : false
                }
                nbrElementParPage={pagination.nbrElementParPage}
                sortColumn={pagination.sortColumn}
                sortOrder={pagination.sortOrder}
                page={pagination.page}
                infoProfil={infoProfil}
                onlyBesse={onlyBesse}
                setSelectedDossier={setSelectedDossier}
                onSetDuplicationDossier={onSetDuplicationDossier}
                onSetAccepterDossier={handleAccepterLocation}
                onSetRefuserDossier={handleRefuserLocation}
                onSetAccepterRefuserDossier={handleAccepterRefuserLocation}
                isAccepterRefuserLocationLoading={
                  isAccepterRefuserLocationLoading
                }
                canAcceptRefuseLocation={
                  infoProfil &&
                  infoProfil.fonctions &&
                  infoProfil.fonctions.find((f) => f === "TDBDOSLOC_VALIDATION")
                    ? true
                    : false
                }
                genererLoading={genererLoading}
              />
            ) : (
              ""
            )}
          </LoadingBoundary>
          <Snack
            onClose={handleClose}
            open={showRetourConfirmationDossierAccepte}
            variant="success"
            message={
              retourLocationAcceptee && retourLocationAcceptee.message
                ? retourLocationAcceptee.message
                : ""
            }
          />
          <Snack
            onClose={onCloseConfirmationCreationDossierLocation}
            open={confirmationCreationDossierLocation}
            variant="success"
            message={
              "Votre dossier a bien été créé et envoyé au candidat locataire"
            }
          />
        </>
      </Grid>
      <Grid container justifyContent="flex-end" style={{ marginTop: 12 }}>
        {accepterRefuserLocationError ? (
          <RenderError
            errors={accepterRefuserLocationError}
            onClose={supprimerErrorDossierLocation}
          />
        ) : (
          ""
        )}
      </Grid>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="confirm-dialog"
      >
        <DialogTitle id="confirm-dialog">
          {"Etes vous sur de vouloir supprimer ce dossier ?"}
        </DialogTitle>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => onCancel()}
            color="secondary"
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => handleConfirm()}
            color="primary"
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Dashboard;
